import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Bot,
  Sparkles,
  Send,
  Database,
  Activity,
  Server,
  Cpu,
  Layers,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  Copy,
  Check,
  Plus,
  Clock,
  ShieldCheck,
  BookOpen,
} from 'lucide-react';
import {
  sendChatMessage,
  getConversationHistory,
  ingestDocuments,
  getSystemHealth,
} from '../services/api';

const PRESET_TEMPLATES = [
  {
    title: 'Enterprise SLA Guarantee',
    id: 'doc-sla-enterprise',
    category: 'sla',
    author: 'operations',
    content:
      'Businex guarantees 99.99% monthly uptime for all Enterprise Tier services. Critical Severity-1 incidents receive an initial response within 15 minutes by a senior solutions architect, with continuous hourly status updates until resolution.',
  },
  {
    title: 'Data Security & AES-256 Encryption',
    id: 'doc-security-compliance',
    category: 'security',
    author: 'infosec',
    content:
      'All customer data at rest is encrypted using AES-256 GCM encryption keys managed under HSM. In-transit communication strictly enforces TLS 1.3. Automated zero-trust security audits and penetration tests are conducted quarterly by independent third-party auditors.',
  },
  {
    title: 'Businex Platform Operating Hours',
    id: 'doc-hours-standard',
    category: 'business_hours',
    author: 'support',
    content:
      'Standard customer success and technical consultation teams are available Monday through Friday from 8:00 AM to 8:00 PM EST. For Enterprise Tier and Mission-Critical accounts, 24/7/365 dedicated hotline and emergency triage is active worldwide.',
  },
];

const AiHub = () => {
  // Tabs: 'chat', 'ingestion', 'health'
  const [activeTab, setActiveTab] = useState('chat');

  // --- Health State ---
  const [healthData, setHealthData] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthError, setHealthError] = useState(null);
  const [healthLatency, setHealthLatency] = useState(null);

  // --- Chat State ---
  const [chatMessages, setChatMessages] = useState([
    {
      id: 'welcome-0',
      sender: 'bot',
      text: 'Welcome to the Businex Enterprise AI Studio. I am grounded with real-time vector search across ChromaDB knowledge chunks and powered by Google Gemini. How can I assist you?',
      timestamp: new Date().toISOString(),
      sources: [],
    },
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId, setSessionId] = useState(
    () =>
      localStorage.getItem('businex_chat_session_id') ||
      'usr-session-' + Math.random().toString(36).substring(2, 9)
  );
  const [copiedSession, setCopiedSession] = useState(false);
  const [selectedMessageSources, setSelectedMessageSources] = useState(null);
  const [dbHistory, setDbHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const messagesEndRef = useRef(null);

  // --- Ingestion State ---
  const [docContent, setDocContent] = useState('');
  const [docId, setDocId] = useState(`doc-kb-${Math.floor(1000 + Math.random() * 9000)}`);
  const [docCategory, setDocCategory] = useState('general');
  const [docAuthor, setDocAuthor] = useState('engineering');
  const [ingestLoading, setIngestLoading] = useState(false);
  const [ingestResult, setIngestResult] = useState(null);
  const [ingestError, setIngestError] = useState(null);

  // Fetch Health
  const fetchHealth = async () => {
    setHealthLoading(true);
    setHealthError(null);
    const start = Date.now();
    try {
      const data = await getSystemHealth();
      setHealthLatency(Date.now() - start);
      setHealthData(data);
    } catch (err) {
      setHealthError(err.message);
    } finally {
      setHealthLoading(false);
    }
  };

  useEffect(() => {
    fetchHealth();
  }, []);

  // Fetch History for current sessionId
  const fetchSessionHistory = useCallback(async () => {
    if (!sessionId) return;
    setLoadingHistory(true);
    try {
      const res = await getConversationHistory(sessionId);
      if (res?.data) {
        setDbHistory(res.data);
      }
    } catch (err) {
      console.warn('History fetch error:', err);
    } finally {
      setLoadingHistory(false);
    }
  }, [sessionId]);

  useEffect(() => {
    fetchSessionHistory();
  }, [fetchSessionHistory]);

  // Scroll chat to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, chatLoading]);

  // Handle Send Chat
  const handleSendChat = async (presetText) => {
    const text = (presetText || chatInput).trim();
    if (!text || chatLoading) return;

    const userMsg = {
      id: 'usr-' + Date.now(),
      sender: 'user',
      text,
      timestamp: new Date().toISOString(),
    };

    setChatMessages((prev) => [...prev, userMsg]);
    if (!presetText) setChatInput('');
    setChatLoading(true);

    try {
      const response = await sendChatMessage({
        message: text,
        sessionId,
      });

      const replyData = response?.data;
      const botText =
        replyData?.reply ||
        response?.reply ||
        'Your query was processed by the Businex conversational pipeline.';

      const newBotMsg = {
        id: 'bot-' + Date.now(),
        sender: 'bot',
        text: botText,
        timestamp: replyData?.timestamp || new Date().toISOString(),
        sources: replyData?.sources || [],
        savedToDb: replyData?.savedToDb,
      };

      setChatMessages((prev) => [...prev, newBotMsg]);

      // Automatically inspect sources in the side panel if available
      if (replyData?.sources && replyData.sources.length > 0) {
        setSelectedMessageSources(replyData.sources);
      }

      // Refresh DB history list
      fetchSessionHistory();
    } catch (err) {
      setChatMessages((prev) => [
        ...prev,
        {
          id: 'err-' + Date.now(),
          sender: 'bot',
          isError: true,
          text: `Error connecting to backend: ${err.message}`,
          timestamp: new Date().toISOString(),
          sources: [],
        },
      ]);
    } finally {
      setChatLoading(false);
    }
  };

  // Handle Ingest Document
  const handleIngest = async (e) => {
    e.preventDefault();
    if (!docContent.trim() || ingestLoading) return;

    setIngestLoading(true);
    setIngestResult(null);
    setIngestError(null);

    try {
      const payload = {
        documents: [docContent.trim()],
        ids: [docId.trim() || `doc-${Date.now()}`],
        metadatas: [
          {
            category: docCategory.trim() || 'general',
            author: docAuthor.trim() || 'admin',
            ingestedAt: new Date().toISOString(),
          },
        ],
      };

      const result = await ingestDocuments(payload);
      setIngestResult(result);
      // Reset input fields with new random ID
      setDocContent('');
      setDocId(`doc-kb-${Math.floor(1000 + Math.random() * 9000)}`);
    } catch (err) {
      setIngestError(err.message);
    } finally {
      setIngestLoading(false);
    }
  };

  // Load Preset into Ingestion form
  const applyPreset = (preset) => {
    setDocContent(preset.content);
    setDocId(preset.id);
    setDocCategory(preset.category);
    setDocAuthor(preset.author);
  };

  // Helper to format uptime
  const formatUptime = (seconds) => {
    if (!seconds && seconds !== 0) return 'N/A';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs}h ${mins}m ${secs}s`;
  };

  return (
    <div className="pt-28 pb-20 min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50 text-slate-900">
      <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
        {/* Header Hero */}
        <div className="mb-10 text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-xs font-semibold uppercase tracking-wider mb-4 shadow-xs">
            <Sparkles size={14} className="text-blue-600 animate-spin [animation-duration:6s]" />
            Enterprise Conversational AI & RAG Engine
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight text-slate-900 mb-4">
            Businex <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600">AI Platform</span> Studio
          </h1>
          <p className="text-slate-600 text-base md:text-lg leading-relaxed">
            Experience our full Retrieval-Augmented Generation pipeline. Grounded with ChromaDB vector search, synthesized by Google Gemini, and persisted in Microsoft SQL Server.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex p-1.5 bg-slate-100/80 backdrop-blur-md rounded-2xl border border-slate-200/80 shadow-inner">
            <button
              onClick={() => setActiveTab('chat')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all ${
                activeTab === 'chat'
                  ? 'bg-white text-blue-600 shadow-md'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Bot size={18} />
              Conversational RAG
            </button>

            <button
              onClick={() => setActiveTab('ingestion')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all ${
                activeTab === 'ingestion'
                  ? 'bg-white text-blue-600 shadow-md'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Database size={18} />
              Vector Ingestion
            </button>

            <button
              onClick={() => {
                setActiveTab('health');
                fetchHealth();
              }}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all ${
                activeTab === 'health'
                  ? 'bg-white text-blue-600 shadow-md'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Activity size={18} />
              System Diagnostics
              {healthData?.status === 'ok' ? (
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              ) : (
                <span className="w-2 h-2 rounded-full bg-amber-500" />
              )}
            </button>
          </div>
        </div>

        {/* TAB 1: Conversational RAG Studio */}
        {activeTab === 'chat' && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Chat Box (8 cols) */}
            <div className="lg:col-span-8 flex flex-col bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden h-[680px]">
              {/* Chat Header */}
              <div className="px-6 py-4 bg-slate-900 text-white flex items-center justify-between border-b border-slate-800">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-blue-500 to-indigo-500 flex items-center justify-center shadow">
                    <Sparkles size={18} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-sm">Grounded Knowledge Chat</h3>
                    <p className="text-[11px] text-slate-300">
                      POST <code className="text-blue-300">/api/chat</code>
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      const newId = 'usr-session-' + Math.random().toString(36).substring(2, 9);
                      setSessionId(newId);
                      setChatMessages([
                        {
                          id: 'welcome-' + Date.now(),
                          sender: 'bot',
                          text: `Switched to new session (${newId}). Ask a question to test conversational RAG memory!`,
                          timestamp: new Date().toISOString(),
                          sources: [],
                        },
                      ]);
                      setSelectedMessageSources(null);
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-xs font-medium text-slate-200 transition-colors"
                  >
                    <Plus size={14} />
                    New Session
                  </button>
                </div>
              </div>

              {/* Session Bar */}
              <div className="px-6 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center justify-between text-xs text-slate-600">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-500">Session ID:</span>
                  <code className="font-mono bg-blue-50 text-blue-700 px-2 py-0.5 rounded border border-blue-200 text-xs">
                    {sessionId}
                  </code>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(sessionId);
                      setCopiedSession(true);
                      setTimeout(() => setCopiedSession(false), 2000);
                    }}
                    className="text-slate-400 hover:text-slate-700"
                    title="Copy Session ID"
                  >
                    {copiedSession ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
                  </button>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[11px] text-slate-400">
                    {chatMessages.filter((m) => m.sender === 'user').length} queries sent
                  </span>
                </div>
              </div>

              {/* Message Feed */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gradient-to-b from-slate-50/50 to-white">
                {chatMessages.map((msg) => {
                  const isUser = msg.sender === 'user';
                  const hasSources = msg.sources && msg.sources.length > 0;

                  return (
                    <div
                      key={msg.id}
                      className={`flex flex-col ${isUser ? 'items-end' : 'items-start'}`}
                    >
                      <div
                        className={`max-w-[85%] p-4 rounded-2xl shadow-xs text-sm leading-relaxed ${
                          isUser
                            ? 'bg-blue-600 text-white rounded-br-xs'
                            : msg.isError
                            ? 'bg-rose-50 border border-rose-200 text-rose-800 rounded-bl-xs'
                            : 'bg-white border border-slate-200/90 text-slate-800 rounded-bl-xs'
                        }`}
                      >
                        <p className="whitespace-pre-line">{msg.text}</p>

                        {/* Citations / Sources Button */}
                        {hasSources && (
                          <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                            <button
                              onClick={() => setSelectedMessageSources(msg.sources)}
                              className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 hover:text-blue-800 bg-blue-50 px-2.5 py-1 rounded-lg transition-colors border border-blue-100"
                            >
                              <BookOpen size={13} className="text-blue-600" />
                              Inspect Grounding Sources ({msg.sources.length})
                            </button>

                            {msg.savedToDb && (
                              <span className="text-[10px] text-emerald-600 font-medium flex items-center gap-1">
                                <CheckCircle2 size={12} /> Persisted in MSSQL
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      <span className="text-[10px] text-slate-400 mt-1 px-1">
                        {new Date(msg.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                  );
                })}

                {chatLoading && (
                  <div className="flex items-center gap-3 bg-white border border-slate-200 px-4 py-3 rounded-2xl rounded-bl-xs w-max shadow-sm">
                    <div className="flex space-x-1.5">
                      <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                      <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                      <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"></div>
                    </div>
                    <span className="text-xs text-slate-500 font-medium">
                      Querying ChromaDB & synthesizing with Gemini...
                    </span>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Suggested Questions */}
              <div className="px-6 py-2 border-t border-slate-100 bg-slate-50/70 flex items-center gap-2 overflow-x-auto text-xs">
                <span className="text-slate-400 shrink-0 font-medium text-[11px]">Suggestions:</span>
                {[
                  'What are your business operating hours?',
                  'What is your SLA response time guarantee?',
                  'How is backup data encrypted?',
                ].map((s, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSendChat(s)}
                    disabled={chatLoading}
                    className="shrink-0 bg-white hover:bg-blue-50 hover:text-blue-700 text-slate-600 px-3 py-1 rounded-full border border-slate-200 transition-colors truncate max-w-[220px]"
                  >
                    {s}
                  </button>
                ))}
              </div>

              {/* Chat Input Bar */}
              <div className="p-4 bg-white border-t border-slate-200">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSendChat();
                  }}
                  className="flex items-center gap-3"
                >
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    placeholder="Ask any question about Businex services, compliance, or architecture..."
                    className="flex-1 px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm transition-all"
                  />
                  <button
                    type="submit"
                    disabled={!chatInput.trim() || chatLoading}
                    className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-5 py-3 rounded-xl font-medium text-sm flex items-center gap-2 shadow-sm transition-colors"
                  >
                    <Send size={16} />
                    <span>Send</span>
                  </button>
                </form>
              </div>
            </div>

            {/* Side Panel: Context Inspector & DB Thread History (4 cols) */}
            <div className="lg:col-span-4 space-y-6">
              {/* Grounding Context Inspector */}
              <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Database size={18} className="text-indigo-600" />
                    <h3 className="font-bold text-slate-900 text-sm">Grounding Context</h3>
                  </div>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-100">
                    ChromaDB
                  </span>
                </div>

                {selectedMessageSources && selectedMessageSources.length > 0 ? (
                  <div className="space-y-3 max-h-[260px] overflow-y-auto pr-1 text-xs">
                    {selectedMessageSources.map((src, i) => (
                      <div
                        key={src.id || i}
                        className="p-3 bg-slate-50 rounded-xl border border-slate-200/80 space-y-1.5"
                      >
                        <div className="flex items-center justify-between font-mono text-[11px] text-slate-500">
                          <span className="font-semibold text-slate-800">
                            {src.id || `chunk-${i + 1}`}
                          </span>
                          {src.distance !== undefined && (
                            <span className="bg-white px-1.5 py-0.5 rounded border border-slate-200 text-[10px]">
                              dist: {Number(src.distance).toFixed(3)}
                            </span>
                          )}
                        </div>
                        <p className="text-slate-700 leading-relaxed italic bg-white p-2 rounded border border-slate-100 text-[11.5px]">
                          "{src.snippet || src.document || 'No snippet preview.'}"
                        </p>
                        {src.metadata && Object.keys(src.metadata).length > 0 && (
                          <div className="flex flex-wrap gap-1 pt-1">
                            {Object.entries(src.metadata).map(([k, v]) => (
                              <span
                                key={k}
                                className="text-[9.5px] bg-slate-200/70 text-slate-700 px-1.5 py-0.5 rounded font-mono"
                              >
                                {k}: {String(v)}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-6 text-center text-slate-400 text-xs border border-dashed border-slate-200 rounded-xl bg-slate-50/50">
                    <BookOpen size={24} className="mx-auto mb-2 text-slate-300" />
                    <p>Send a question to view the semantic knowledge chunks retrieved by ChromaDB.</p>
                  </div>
                )}
              </div>

              {/* MSSQL Conversation History Panel */}
              <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Clock size={18} className="text-blue-600" />
                    <h3 className="font-bold text-slate-900 text-sm">Session History in MSSQL</h3>
                  </div>
                  <button
                    onClick={fetchSessionHistory}
                    disabled={loadingHistory}
                    className="text-slate-400 hover:text-blue-600 transition-colors p-1"
                    title="Refresh DB history"
                  >
                    <RefreshCw size={14} className={loadingHistory ? 'animate-spin' : ''} />
                  </button>
                </div>

                <p className="text-[11px] text-slate-500 mb-3">
                  Fetched via <code className="text-blue-600 font-mono">GET /api/history/:sessionId</code>
                </p>

                <div className="space-y-2.5 max-h-[240px] overflow-y-auto pr-1 text-xs">
                  {loadingHistory ? (
                    <div className="py-6 text-center text-slate-400">Loading history...</div>
                  ) : dbHistory.length === 0 ? (
                    <div className="p-4 text-center text-slate-400 text-xs bg-slate-50 rounded-xl border border-slate-100">
                      No prior exchanges stored in MSSQL for this session yet.
                    </div>
                  ) : (
                    dbHistory.map((item, i) => (
                      <div
                        key={item.id || i}
                        className="p-2.5 bg-slate-50 hover:bg-slate-100/80 rounded-xl border border-slate-100 transition-colors space-y-1"
                      >
                        <div className="font-semibold text-slate-800 text-[11.5px] truncate">
                          Q: {item.userMessage}
                        </div>
                        <div className="text-slate-600 text-[11px] line-clamp-2">
                          A: {item.botMessage}
                        </div>
                        <div className="text-[9px] text-slate-400 font-mono">
                          {new Date(item.timestamp).toLocaleString()}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 2: ChromaDB Knowledge Ingestion */}
        {activeTab === 'ingestion' && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Form (7 cols) */}
            <div className="lg:col-span-7 bg-white rounded-3xl border border-slate-200 shadow-sm p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
                <div className="w-10 h-10 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center">
                  <Database size={20} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Ingest Knowledge into ChromaDB</h2>
                  <p className="text-xs text-slate-500">
                    Endpoint: <code className="text-purple-600 font-mono">POST /api/documents</code>
                  </p>
                </div>
              </div>

              {/* Status alerts */}
              {ingestResult && (
                <div className="mb-6 p-4 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800 flex items-start gap-3">
                  <CheckCircle2 size={20} className="text-emerald-600 shrink-0 mt-0.5" />
                  <div className="text-xs">
                    <p className="font-semibold text-sm">{ingestResult.message}</p>
                    <p className="mt-1 text-emerald-700">
                      Indexed {ingestResult.data?.count || 1} document(s). Newly added knowledge will now be used by Gemini to ground responses!
                    </p>
                    {ingestResult.data?.ids && (
                      <p className="font-mono text-[10px] mt-1 text-emerald-600">
                        IDs: {ingestResult.data.ids.join(', ')}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {ingestError && (
                <div className="mb-6 p-4 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 flex items-start gap-3">
                  <AlertTriangle size={20} className="text-rose-600 shrink-0 mt-0.5" />
                  <div className="text-xs">
                    <p className="font-semibold text-sm">Ingestion Failed</p>
                    <p className="mt-1">{ingestError}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleIngest} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                      Document ID
                    </label>
                    <input
                      type="text"
                      value={docId}
                      onChange={(e) => setDocId(e.target.value)}
                      placeholder="doc-sla-01"
                      className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-xs focus:bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                      Category Tag
                    </label>
                    <input
                      type="text"
                      value={docCategory}
                      onChange={(e) => setDocCategory(e.target.value)}
                      placeholder="e.g. sla, security"
                      className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-xs focus:bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                      Author Tag
                    </label>
                    <input
                      type="text"
                      value={docAuthor}
                      onChange={(e) => setDocAuthor(e.target.value)}
                      placeholder="e.g. devops, support"
                      className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-xs focus:bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Knowledge Document Content
                  </label>
                  <textarea
                    rows={6}
                    value={docContent}
                    onChange={(e) => setDocContent(e.target.value)}
                    placeholder="Enter proprietary facts, policy text, service details, or FAQ content to ingest into the vector store..."
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  />
                  <p className="text-[11px] text-slate-400 mt-1">
                    This text will be converted into high-dimensional vector embeddings and indexed in ChromaDB's <code className="font-mono text-purple-600">rag_knowledge_base</code> collection.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={!docContent.trim() || ingestLoading}
                  className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-semibold py-3.5 rounded-xl transition-colors shadow-sm flex items-center justify-center gap-2 text-sm"
                >
                  {ingestLoading ? (
                    <>
                      <RefreshCw size={16} className="animate-spin" />
                      Vectorizing & Ingesting into ChromaDB...
                    </>
                  ) : (
                    <>
                      <Plus size={16} />
                      Ingest Document into ChromaDB
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Presets & Explanation (5 cols) */}
            <div className="lg:col-span-5 space-y-6">
              <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6">
                <h3 className="font-bold text-slate-900 text-sm mb-3 flex items-center gap-2">
                  <Sparkles size={16} className="text-purple-600" />
                  Quick Ingestion Presets
                </h3>
                <p className="text-xs text-slate-500 mb-4">
                  Click any template to pre-populate the form and test RAG grounding immediately:
                </p>

                <div className="space-y-3">
                  {PRESET_TEMPLATES.map((preset, idx) => (
                    <div
                      key={idx}
                      className="p-3.5 bg-slate-50 hover:bg-purple-50/50 rounded-2xl border border-slate-200/70 transition-all cursor-pointer group"
                      onClick={() => applyPreset(preset)}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold text-xs text-slate-800 group-hover:text-purple-700">
                          {preset.title}
                        </span>
                        <span className="text-[9.5px] font-mono uppercase bg-white px-1.5 py-0.5 rounded border text-slate-600">
                          {preset.category}
                        </span>
                      </div>
                      <p className="text-[11.5px] text-slate-600 line-clamp-2 italic">
                        "{preset.content}"
                      </p>
                      <span className="text-[10px] text-purple-600 font-semibold mt-1 inline-block group-hover:underline">
                        Apply this template &rarr;
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* RAG Mechanics Infobox */}
              <div className="bg-gradient-to-br from-slate-900 to-blue-950 text-white rounded-3xl p-6 shadow-md">
                <h4 className="font-bold text-sm mb-2 flex items-center gap-2">
                  <Layers size={16} className="text-blue-400" />
                  How Ingestion Enhances RAG
                </h4>
                <ol className="text-xs text-slate-300 space-y-2 list-decimal list-inside leading-relaxed">
                  <li>Your text is received at <code className="text-blue-300 font-mono">POST /api/documents</code>.</li>
                  <li>Gemini converts the snippet into a high-dimensional dense vector embedding.</li>
                  <li>ChromaDB indexes the embedding alongside metadata.</li>
                  <li>When visitors ask questions, ChromaDB computes nearest cosine neighbors to supply relevant facts to Gemini!</li>
                </ol>
              </div>
            </div>
          </div>
        )}

        {/* TAB 3: System Health & Diagnostics */}
        {activeTab === 'health' && (
          <div className="space-y-8">
            {/* Top overview card */}
            <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 sm:p-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-2xl font-bold text-slate-900">System Diagnostic Status</h2>
                  {healthLoading ? (
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 flex items-center gap-1.5">
                      <RefreshCw size={12} className="animate-spin" /> Pinging...
                    </span>
                  ) : healthData?.status === 'ok' ? (
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 flex items-center gap-1.5">
                      <CheckCircle2 size={14} className="text-emerald-600" /> Operational
                    </span>
                  ) : (
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 flex items-center gap-1.5">
                      <AlertTriangle size={14} className="text-amber-600" /> {healthData?.status || 'Degraded'}
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500">
                  Endpoint: <code className="font-mono text-blue-600">GET /api/health</code> &bull; Server: <code className="font-mono text-slate-700">http://localhost:3000</code>
                  {healthLatency !== null && ` (${healthLatency}ms ping)`}
                </p>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={fetchHealth}
                  disabled={healthLoading}
                  className="px-5 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold transition-colors flex items-center gap-2 shadow-xs"
                >
                  <RefreshCw size={14} className={healthLoading ? 'animate-spin' : ''} />
                  Refresh Diagnostics
                </button>
              </div>
            </div>

            {healthError && (
              <div className="p-4 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-center gap-3">
                <AlertTriangle size={18} className="text-rose-600 shrink-0" />
                <div>
                  <span className="font-bold">Failed to reach API server: </span>
                  {healthError}. Make sure the backend server on port 3000 is active.
                </div>
              </div>
            )}

            {/* Diagnostic Component Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* 1. API Component */}
              <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center">
                    <Server size={20} />
                  </div>
                  <span
                    className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                      healthData?.components?.api?.status === 'up'
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                        : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    {healthData?.components?.api?.status || 'Unknown'}
                  </span>
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">Express API Server</h4>
                  <p className="text-xs text-slate-500 mt-1">REST API Gateway & Controllers</p>
                </div>
                <div className="pt-3 border-t border-slate-100 text-xs space-y-1.5 text-slate-600">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Port:</span>
                    <span className="font-mono font-semibold">3000</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Uptime:</span>
                    <span className="font-mono">{formatUptime(healthData?.uptimeSeconds)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Environment:</span>
                    <span className="font-mono">Node.js ES Modules</span>
                  </div>
                </div>
              </div>

              {/* 2. Database Component */}
              <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                    <Database size={20} />
                  </div>
                  <span
                    className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                      healthData?.components?.database?.status === 'connected'
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                        : 'bg-amber-50 text-amber-700 border border-amber-200'
                    }`}
                  >
                    {healthData?.components?.database?.status || 'Offline'}
                  </span>
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">Microsoft SQL Server</h4>
                  <p className="text-xs text-slate-500 mt-1">Sequelize ORM & History Persistence</p>
                </div>
                <div className="pt-3 border-t border-slate-100 text-xs space-y-1.5 text-slate-600">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Dialect:</span>
                    <span className="font-mono font-semibold uppercase">
                      {healthData?.components?.database?.dialect || 'mssql'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Table:</span>
                    <span className="font-mono">Conversations</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Session Memory:</span>
                    <span className="text-emerald-600 font-medium">Multi-turn Active</span>
                  </div>
                </div>
              </div>

              {/* 3. ChromaDB Vector Store */}
              <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-2xl bg-purple-50 text-purple-600 flex items-center justify-center">
                    <Layers size={20} />
                  </div>
                  <span
                    className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                      healthData?.components?.vectorStore?.status === 'connected'
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                        : 'bg-amber-50 text-amber-700 border border-amber-200'
                    }`}
                  >
                    {healthData?.components?.vectorStore?.status || 'Disconnected'}
                  </span>
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">ChromaDB Vector Store</h4>
                  <p className="text-xs text-slate-500 mt-1">High-Dimensional Knowledge Search</p>
                </div>
                <div className="pt-3 border-t border-slate-100 text-xs space-y-1.5 text-slate-600">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Collection:</span>
                    <span className="font-mono text-purple-700 font-semibold truncate max-w-[120px]">
                      {healthData?.components?.vectorStore?.collection || 'rag_knowledge_base'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Vector Index:</span>
                    <span className="font-mono">HNSW / Cosine</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Top-K Retrieval:</span>
                    <span className="font-mono font-medium">Active (k=4)</span>
                  </div>
                </div>
              </div>

              {/* 4. Google AI LLM */}
              <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center">
                    <Cpu size={20} />
                  </div>
                  <span
                    className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                      healthData?.components?.llm?.configured
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                        : 'bg-rose-50 text-rose-700 border border-rose-200'
                    }`}
                  >
                    {healthData?.components?.llm?.configured ? 'Configured' : 'Missing Key'}
                  </span>
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">Google AI (Gemini)</h4>
                  <p className="text-xs text-slate-500 mt-1">Inference & Vector Embeddings</p>
                </div>
                <div className="pt-3 border-t border-slate-100 text-xs space-y-1.5 text-slate-600">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Model:</span>
                    <span className="font-mono font-semibold text-amber-700 truncate max-w-[120px]">
                      {healthData?.components?.llm?.model || 'gemini-1.5-flash'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Embeddings:</span>
                    <span className="font-mono truncate max-w-[120px]">
                      {healthData?.components?.llm?.embeddingModel || 'text-embedding-004'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Grounding:</span>
                    <span className="text-emerald-600 font-medium">Active</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Architecture Details Box */}
            <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8">
              <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <ShieldCheck size={20} className="text-blue-600" />
                Enterprise Architectural Specifications
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-xs text-slate-600">
                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100">
                  <h5 className="font-bold text-slate-800 text-sm mb-1.5">1. Semantic Retrieval</h5>
                  <p className="leading-relaxed">
                    User prompts are vectorized and matched against ChromaDB embeddings via cosine similarity to fetch only verified facts.
                  </p>
                </div>
                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100">
                  <h5 className="font-bold text-slate-800 text-sm mb-1.5">2. Grounded Synthesis</h5>
                  <p className="leading-relaxed">
                    Google Gemini synthesizes responses strictly constrained to retrieved context chunks, preventing hallucinations.
                  </p>
                </div>
                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100">
                  <h5 className="font-bold text-slate-800 text-sm mb-1.5">3. Audit & Persistence</h5>
                  <p className="leading-relaxed">
                    Every interaction, session identifier, and context snippet is audited and stored in Microsoft SQL Server via Sequelize.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AiHub;
